import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { ArrowLeft, Plus, Trash2, Loader2, Save } from "lucide-react";

const OPTION_TITLES = {
  canal: "Canal",
  servicio: "Servicio",
  conocio: "Cómo nos conoció",
};

const OPTION_FIELDS = {
  canal: "canal",
  servicio: "servicio",
  conocio: "como_conocio",
};

export default function LeadOptionsEditor({ type, options, leads, onSave, onBack, saving }) {
  const [items, setItems] = useState(options.map(o => ({ ...o })));
  const [deleteError, setDeleteError] = useState(null);
  const [visible, setVisible] = useState(false);
  const field = OPTION_FIELDS[type];

  useEffect(() => {
    requestAnimationFrame(() => setVisible(true));
  }, []);

  const addItem = () => {
    const maxOrder = items.length > 0 ? Math.max(...items.map(i => i.sort_order)) : 0;
    setItems([...items, { id: `new-${Date.now()}`, name: "", sort_order: maxOrder + 1, isNew: true }]);
  };

  const updateItem = (idx, value) => {
    const newItems = [...items];
    newItems[idx] = { ...newItems[idx], name: value };
    setItems(newItems);
  };

  const removeItem = (idx) => {
    const item = items[idx];
    if (!item.isNew) {
      const usedBy = leads.filter(l => l[field] === item.name);
      if (usedBy.length > 0) {
        setDeleteError(`No se puede eliminar "${item.name}" porque está siendo usada por ${usedBy.length} lead(s).`);
        return;
      }
    }
    setDeleteError(null);
    setItems(items.filter((_, i) => i !== idx));
  };

  const handleSave = () => {
    const validItems = items.filter(i => i.name.trim());
    onSave(type, validItems);
  };

  const handleBack = () => {
    setVisible(false);
    setTimeout(onBack, 200);
  };

  const content = (
    <>
      <div className="flex items-center gap-3 px-6 py-4 border-b border-gray-100">
        <button onClick={handleBack} className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors">
          <ArrowLeft size={18} className="text-gray-500" />
        </button>
        <span className="text-base font-semibold text-gray-900">
          Opciones: {OPTION_TITLES[type]}
        </span>
      </div>

      <div className="flex-1 px-6 py-4 overflow-y-auto">
        {deleteError && (
          <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-700">
            {deleteError}
          </div>
        )}

        {items.length === 0 ? (
          <p className="text-sm text-gray-400 text-center py-8">No hay opciones configuradas</p>
        ) : (
          <div className="space-y-2">
            {items.map((item, idx) => (
              <div key={item.id || idx} className="flex items-center gap-2">
                <input
                  type="text"
                  value={item.name}
                  onChange={(e) => updateItem(idx, e.target.value)}
                  placeholder="Nombre de la opción"
                  className="flex-1 px-3 py-2.5 text-sm border border-gray-200 rounded-lg outline-none focus:border-brand"
                />
                <button
                  onClick={() => removeItem(idx)}
                  className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            ))}
          </div>
        )}

        <button
          onClick={addItem}
          className="mt-4 w-full py-2.5 text-sm text-brand border border-dashed border-brand/40 rounded-lg hover:bg-brand-light flex items-center justify-center gap-1.5 transition-colors"
        >
          <Plus size={16} /> Añadir opción
        </button>
      </div>

      <div className="px-6 py-4 border-t border-gray-100 flex gap-3">
        <button
          onClick={handleBack}
          className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-xl transition-colors"
        >
          Cancelar
        </button>
        <button
          onClick={handleSave}
          disabled={saving}
          className="flex-1 px-4 py-2.5 text-sm font-medium text-white bg-brand hover:bg-brand-dark rounded-xl transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
        >
          {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
          Guardar
        </button>
      </div>
    </>
  );

  const drawer = (
    <div className="fixed inset-0 z-[999999]">
      <div
        className={`
          hidden md:flex fixed top-0 right-0 h-full w-full max-w-md
          flex-col bg-white shadow-2xl border-l border-gray-200
          transition-transform duration-200 ease-out
          ${visible ? "translate-x-0" : "translate-x-full"}
        `}
      >
        {content}
      </div>

      <div
        className={`
          md:hidden fixed inset-x-0 bottom-0 top-0 bg-white
          flex flex-col
          transition-transform duration-200 ease-out
          ${visible ? "translate-y-0" : "translate-y-full"}
        `}
      >
        {content}
      </div>
    </div>
  );

  return createPortal(drawer, document.body);
}
